/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';

import {AngularViewRefreshService} from '../common/util/angular-view-refresh.service';
import {
  findCreativeAdvertiserMismatch,
  findFolioCreativeAdvertisersMismatch,
  findOrderCreativeAdvertisersMismatch,
  isFolioAdvertiserMismatchWarning
} from './creative-advertiser-mismatch';
import {CreativeAdvertiserMismatchService} from './creative-advertiser-mismatch.service';

@Component({
  standalone: false,
  selector: 'adn-creative-advertiser-mismatch-fix',
  template: `
    <p *ngIf="visible">
      <a (click)="updateAdvertiser()">{{ updating ? 'Updating advertiser…' : linkText }}</a>
    </p>
  `
})
export class CreativeAdvertiserMismatchFixComponent implements OnChanges {
  @Input() creative: any;
  @Input() order: any;
  @Input() folio: any;
  @Input() warnings: any;
  @Input() readOnly: boolean;
  @Input() libraryCreative = false;
  @Output() updated = new EventEmitter<any>();

  visible = false;
  updating = false;
  linkText = 'Update the advertiser to match';

  constructor(
    private readonly mismatchService: CreativeAdvertiserMismatchService,
    private readonly viewRefresh: AngularViewRefreshService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.warnings || changes.creative || changes.order || changes.folio || changes.readOnly) {
      this.syncVisibility();
    }
  }

  updateAdvertiser(): void {
    if (this.updating || this.readOnly) {
      return;
    }
    const update = this.folio?.id && !this.creative?.id
      ? this.mismatchService.updateFolioCreativesAdvertiser(this.folio)
      : this.order?.id && !this.creative?.id
        ? this.mismatchService.updateOrderCreativesAdvertiser(this.order)
        : this.creative?.id
          ? this.mismatchService.updateAdvertiser(this.creative, {libraryCreative: this.libraryCreative})
          : null;
    if (!update) {
      return;
    }
    this.updating = true;
    update.then((saved) => {
      this.updating = false;
      this.syncVisibility();
      this.updated.emit(saved);
      this.viewRefresh.schedule();
      this.cdr.markForCheck();
    }, () => {
      this.updating = false;
      this.cdr.markForCheck();
    });
  }

  private syncVisibility(): void {
    const warnings = this.warnings
      || this.creative?.validationWarnings
      || this.order?.validationWarnings
      || this.folio?.validationWarnings;
    const orderMismatch = findOrderCreativeAdvertisersMismatch(warnings);
    if (orderMismatch && this.order?.id && !this.readOnly) {
      this.visible = true;
      this.linkText = 'Update the advertiser on the affected creatives to match the order';
      return;
    }
    const folioMismatch = findFolioCreativeAdvertisersMismatch(warnings);
    if (folioMismatch && this.folio?.id && !this.readOnly) {
      this.visible = true;
      this.linkText = 'Update the advertiser on the affected creatives to match the folio';
      return;
    }
    const mismatch = findCreativeAdvertiserMismatch(warnings);
    this.visible = !!mismatch && !!this.creative?.id && !this.readOnly;
    this.linkText = isFolioAdvertiserMismatchWarning(mismatch)
      ? 'Update the advertiser to match the folio'
      : 'Update the advertiser to match the order';
  }
}
